import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Users, Search, Download, Trash2, Phone, Mail,
  FileText, TrendingUp, Sparkles, Eye,
} from 'lucide-react';

const STATUS_LABELS = {
  new: { label: 'Nouveau', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  contacted: { label: 'Contacté', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  qualified: { label: 'Qualifié', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  converted: { label: 'Converti', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  lost: { label: 'Perdu', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const SOURCE_LABELS = {
  programme_pdf: 'Téléchargement programme',
  contact_form: 'Formulaire de contact',
  newsletter: 'Newsletter',
};

const formatSource = (src) => {
  if (!src) return '—';
  const [prefix] = src.split(':');
  return SOURCE_LABELS[prefix] || prefix;
};

const formatPhone = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

export const LeadsMarketing = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ search: '', status: 'all', source_prefix: 'all' });
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.search.trim()) params.search = filter.search.trim();
      if (filter.status !== 'all') params.status = filter.status;
      if (filter.source_prefix !== 'all') params.source_prefix = filter.source_prefix;
      const [list, st] = await Promise.all([
        api.listMarketingLeads(params),
        api.getMarketingLeadsStats(),
      ]);
      setLeads(list);
      setStats(st);
    } catch (e) {
      toast.error('Erreur de chargement des leads');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.source_prefix]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Supprimer le lead ${lead.first_name} ${lead.last_name} ?`)) return;
    try {
      await api.deleteMarketingLead(lead.id);
      toast.success('Lead supprimé');
      if (active?.id === lead.id) setActive(null);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-8" data-testid="leads-marketing">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">LEADS MARKETING</p>
        <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
          Prospects &amp; <span className="font-serif italic text-[#C9B896]">téléchargements.</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Centralise les prospects qui ont téléchargé un programme PDF ou rempli un formulaire de contact.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total leads" value={stats.total} kicker="cumulés" Icon={Users} />
          <StatCard
            label="7 derniers jours"
            value={stats.last_7_days}
            kicker={
              stats.previous_7_days > 0
                ? `vs ${stats.previous_7_days} la semaine d'avant`
                : 'nouveaux contacts'
            }
            Icon={TrendingUp}
          />
          <StatCard
            label="Convertis"
            value={stats.by_status?.converted || 0}
            kicker="formations signées"
            Icon={Sparkles}
            highlight
          />
          <StatCard
            label="Téléchargements"
            value={(stats.by_source || []).find((s) => s.source === 'programme_pdf')?.count || 0}
            kicker="programmes PDF"
            Icon={FileText}
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <form onSubmit={onSearch} className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Nom, email, téléphone…"
            className="pl-9 h-9"
            data-testid="leads-search"
          />
        </form>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-44 h-9" data-testid="leads-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.source_prefix} onValueChange={(v) => setFilter({ ...filter, source_prefix: v })}>
          <SelectTrigger className="w-56 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <a
          href={api.exportMarketingLeadsUrl()}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-4 py-2 text-sm font-medium"
          data-testid="leads-export-csv"
        >
          <Download className="w-4 h-4" /> Export CSV
        </a>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 border-b border-[#1F2A4A]/8 last:border-0 animate-pulse bg-[#FAF7F0]/40" />)}
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 bg-white border border-[#1F2A4A]/8 rounded-2xl">
          <Users className="w-12 h-12 text-[#C9B896] mx-auto mb-3 opacity-50" />
          <h3 className="text-lg font-medium text-foreground">Aucun lead pour le moment</h3>
          <p className="text-sm text-muted-foreground mt-1">Les téléchargements et formulaires apparaîtront ici.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F2A4A]/10 bg-[#FAF7F0]/50">
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">Contact</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">Source / Formation</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">Date</th>
                <th className="text-left p-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">Statut</th>
                <th className="text-right p-3 text-[10px] font-mono uppercase tracking-wider text-[#C9B896]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#1F2A4A]/8 last:border-0 hover:bg-[#FAF7F0]/40" data-testid={`lead-row-${l.id}`}>
                  <td className="p-3">
                    <p className="font-medium text-[#1F2A4A] text-sm">{l.first_name} {l.last_name}</p>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                      <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{formatPhone(l.phone)}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge className="bg-[#C9B896]/15 text-[#1F2A4A] border-[#C9B896]/30 text-[10px] uppercase tracking-wider">
                      {formatSource(l.source)}
                    </Badge>
                    {l.course_title && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{l.course_title}</p>
                    )}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {l.created_at ? format(parseISO(l.created_at), 'dd MMM yyyy · HH:mm', { locale: fr }) : '—'}
                  </td>
                  <td className="p-3">
                    <Badge className={`text-[10px] border ${STATUS_LABELS[l.status]?.cls || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {STATUS_LABELS[l.status]?.label || l.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setActive(l)} className="p-1.5 hover:bg-[#1F2A4A]/5 rounded-full text-[#1F2A4A]/70" title="Voir" data-testid={`lead-view-${l.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(l)} className="p-1.5 hover:bg-red-50 rounded-full text-red-500/70" title="Supprimer" data-testid={`lead-delete-${l.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <LeadDetailDialog
          lead={active}
          onClose={() => setActive(null)}
          onChanged={async () => { await load(); }}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, kicker, Icon, highlight }) => (
  <div className={`rounded-2xl p-5 border ${highlight ? 'bg-gradient-to-br from-[#C9B896]/15 to-white border-[#C9B896]/40' : 'bg-white border-[#1F2A4A]/10'}`}>
    <Icon className="w-4 h-4 text-[#C9B896] mb-3" />
    <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{value}</p>
    <p className="text-sm font-medium text-[#1F2A4A] mt-2">{label}</p>
    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">{kicker}</p>
  </div>
);

// ─────────────────────────────────────────── Detail dialog
const LeadDetailDialog = ({ lead, onClose, onChanged }) => {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateMarketingLead(lead.id, { status, notes });
      toast.success('Lead mis à jour');
      await onChanged();
      onClose();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-xl" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {lead.first_name} {lead.last_name}
          </DialogTitle>
          <DialogDescription asChild>
            <div>
              <Badge className="bg-[#C9B896]/15 text-[#1F2A4A] border-[#C9B896]/30 mr-2">{formatSource(lead.source)}</Badge>
              {lead.created_at && (
                <span className="text-xs">
                  {format(parseISO(lead.created_at), "'le' dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#FAF7F0]/60 rounded-xl p-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896] mb-1">Email</p>
              <a href={`mailto:${lead.email}`} className="text-sm text-[#1F2A4A] hover:text-[#C9B896] truncate block">
                {lead.email}
              </a>
            </div>
            <div className="bg-[#FAF7F0]/60 rounded-xl p-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896] mb-1">Téléphone</p>
              <a href={`tel:${lead.phone}`} className="text-sm text-[#1F2A4A] hover:text-[#C9B896]">
                {formatPhone(lead.phone)}
              </a>
            </div>
          </div>

          {lead.course_title && (
            <div className="bg-[#FAF7F0]/60 rounded-xl p-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#C9B896] mb-1">Formation demandée</p>
              <p className="text-sm text-[#1F2A4A]">{lead.course_title}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="lead-detail-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Notes internes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Suivi commercial, devis envoyé, prochain RDV…"
              className="min-h-[100px]"
              data-testid="lead-detail-notes"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1F2A4A] text-white hover:bg-[#324166] rounded-full" data-testid="lead-detail-save">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
